import { z } from "zod";
import { SensitivityClass } from "./classification";

/**
 * Data Exposure Contract — the canonical, versioned, machine-readable policy
 * describing EXACTLY what may leave the provider's network, and in what form.
 *
 * The connector enforces it locally (before data leaves), ingestion
 * re-validates it (defence in depth), and the serving layer only knows the
 * resulting Serving Projection fields.
 */

export const IDENT = /^[a-z][a-z0-9_]{0,62}$/;
export const Identifier = z.string().regex(IDENT, "lower_snake_case identifier, max 63 chars");
/** Source identifiers are arbitrary but bounded; they are ALWAYS quoted by adapters, never interpolated raw. */
export const SourceIdentifier = z.string().min(1).max(128).regex(/^[^\u0000"`;\\]+$/, "illegal characters in source identifier");

export const ServingFieldType = z.enum(["string", "integer", "decimal", "boolean", "timestamp", "date", "json", "geo_point"]);
export type ServingFieldType = z.infer<typeof ServingFieldType>;

export const Transform = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("copy") }),
  z.object({ kind: z.literal("round"), decimals: z.number().int().min(-6).max(6) }),
  z.object({ kind: z.literal("bucket"), size: z.number().positive() }),
  z.object({ kind: z.literal("truncate_date"), unit: z.enum(["hour", "day", "week", "month", "year"]) }),
  /** keyed HMAC with a provider-held secret: stable join key, not reversible by DBIndex */
  z.object({ kind: z.literal("hash"), keyRef: z.literal("connector_local") }),
  z.object({ kind: z.literal("tokenise"), keyRef: z.literal("connector_local") }),
  z.object({ kind: z.literal("redact"), replacement: z.string().max(32).default("[redacted]") }),
  z.object({ kind: z.literal("generalise"), mapping: z.enum(["geo_city", "geo_region", "geo_country", "postcode_prefix3", "age_band"]) }),
  z.object({ kind: z.literal("map_values"), map: z.record(z.string(), z.string()).refine((m) => Object.keys(m).length <= 256) , fallback: z.string().nullable().default(null) }),
]);
export type Transform = z.infer<typeof Transform>;

export const FieldDecision = z.enum(["SHARED", "TRANSFORMED", "BLOCKED"]);
export type FieldDecision = z.infer<typeof FieldDecision>;

export const FieldMapping = z.object({
  sourceColumn: SourceIdentifier,
  servingField: Identifier.nullable(), // null when BLOCKED
  decision: FieldDecision,
  sensitivity: SensitivityClass,
  servingType: ServingFieldType.optional(),
  transform: Transform.default({ kind: "copy" }),
  /** True when a human overrode a default-deny classification. Requires an audit event + reason. */
  override: z.object({ by: z.string(), at: z.string(), reason: z.string().min(10) }).optional(),
  filterable: z.boolean().default(false),
  sortable: z.boolean().default(false),
  searchable: z.boolean().default(false),
  description: z.string().max(500).optional(),
});
export type FieldMapping = z.infer<typeof FieldMapping>;

export const RowFilter = z.object({
  column: SourceIdentifier,
  op: z.enum(["eq", "neq", "in", "gte", "lte", "is_null", "not_null"]),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))]).optional(),
});

export const EntityMapping = z.object({
  servingEntity: Identifier,
  sourceSchema: SourceIdentifier.default("public"),
  sourceTable: SourceIdentifier,
  /** Source column(s) that uniquely identify a row; used for UPSERT/DELETE. Hashed if not themselves SHARED. */
  primaryKey: z.array(SourceIdentifier).min(1).max(4),
  /** Change-tracking cursor (updated_at, monotonic id, ...). */
  cursorColumn: SourceIdentifier.optional(),
  rowFilters: z.array(RowFilter).max(16).default([]),
  fields: z.array(FieldMapping).min(1).max(512),
  /** Partition key in the serving layer (a serving field name). */
  partitionBy: Identifier.optional(),
  description: z.string().max(1000).optional(),
});
export type EntityMapping = z.infer<typeof EntityMapping>;

export const FilterOp = z.enum(["eq", "neq", "in", "lt", "lte", "gt", "gte", "prefix", "contains"]);
export type FilterOp = z.infer<typeof FilterOp>;

/** A named, typed operation consumers may invoke. The ONLY way data is read. */
export const Capability = z.object({
  name: Identifier, // e.g. search_products
  description: z.string().min(5).max(500),
  entity: Identifier,
  kind: z.enum(["lookup", "search", "aggregate"]),
  /** Allowed filter fields and operators. Anything else is rejected at plan validation. */
  filters: z.array(z.object({ field: Identifier, ops: z.array(FilterOp).min(1), required: z.boolean().default(false) })).max(32),
  returns: z.array(Identifier).min(1).max(128),
  sort: z.array(z.object({ field: Identifier, dirs: z.array(z.enum(["asc", "desc"])).min(1) })).max(8).default([]),
  aggregate: z
    .object({
      fn: z.enum(["count", "sum", "avg", "min", "max"]),
      field: Identifier.optional(),
      groupBy: z.array(Identifier).max(4).default([]),
      /** k-anonymity floor: groups with fewer rows are suppressed. */
      minGroupSize: z.number().int().min(1).default(5),
    })
    .optional(),
  maxRows: z.number().int().min(1).max(1000).default(100),
  examples: z.array(z.string().max(300)).max(10).default([]),
});
export type Capability = z.infer<typeof Capability>;

export const Freshness = z.enum(["cdc", "1s", "5s", "10s", "30s", "1m", "5m", "15m", "1h", "1d", "manual"]);
export type Freshness = z.infer<typeof Freshness>;
export const FRESHNESS_SECONDS: Record<Freshness, number | null> = {
  cdc: 1, "1s": 1, "5s": 5, "10s": 10, "30s": 30, "1m": 60, "5m": 300, "15m": 900, "1h": 3600, "1d": 86400, manual: null,
};

export const UsageRights = z.object({
  liveQuery: z.boolean().default(true),
  rag: z.boolean().default(false),
  batchData: z.boolean().default(false),
  training: z.boolean().default(false),
  commercialUse: z.boolean().default(true),
  displayToEndUser: z.boolean().default(true),
  derivedAnalytics: z.boolean().default(true),
  redistribution: z.boolean().default(false),
  maxRetentionSeconds: z.number().int().min(0).default(86400),
  attributionRequired: z.boolean().default(true),
  licenseId: z.string().max(64).default("dbindex-standard-query-1.0"),
});
export type UsageRights = z.infer<typeof UsageRights>;

export const ExposureContract = z.object({
  contractId: z.string(),
  version: z.number().int().min(1),
  providerOrgId: z.string(),
  dataSourceId: z.string(),
  datasetId: z.string(),
  mode: z.enum(["materialised", "live_origin"]).default("materialised"),
  freshness: Freshness,
  entities: z.array(EntityMapping).min(1).max(64),
  capabilities: z.array(Capability).min(1).max(64),
  cache: z.object({
    scope: z.enum(["PUBLIC", "DATASET", "CONSUMER", "NO_CACHE"]).default("DATASET"),
    maxTtlSeconds: z.number().int().min(0).max(86400).default(60),
  }),
  rights: UsageRights,
  jurisdiction: z.string().max(16).default("AU"),
  approvedBy: z.array(z.object({ userId: z.string(), role: z.string(), at: z.string() })).default([]),
  createdAt: z.string(),
  /** Hash of the previous version (hash-chained history). */
  previousHash: z.string().nullable().default(null),
});
export type ExposureContract = z.infer<typeof ExposureContract>;

/**
 * Structural invariants beyond the schema. Returns a list of violations; empty means valid.
 * These rules are enforced in the control plane, the connector AND ingestion.
 */
export function validateContractInvariants(c: ExposureContract): string[] {
  const errs: string[] = [];
  const entities = new Map<string, Set<string>>();
  for (const e of c.entities) {
    if (entities.has(e.servingEntity)) errs.push(`duplicate serving entity ${e.servingEntity}`);
    const fields = new Set<string>();
    for (const f of e.fields) {
      if (f.decision === "BLOCKED") {
        if (f.servingField !== null) errs.push(`${e.sourceTable}.${f.sourceColumn}: BLOCKED field must not have a serving name`);
        continue;
      }
      if (!f.servingField) { errs.push(`${e.sourceTable}.${f.sourceColumn}: exported field needs a serving name`); continue; }
      if (fields.has(f.servingField)) errs.push(`${e.servingEntity}.${f.servingField}: duplicate serving field`);
      fields.add(f.servingField);
      if (f.sensitivity === "AUTHENTICATION_SECRET" || f.sensitivity === "PROHIBITED")
        errs.push(`${e.sourceTable}.${f.sourceColumn}: ${f.sensitivity} can never be exported`);
      const denied = ["INTERNAL", "CONFIDENTIAL", "PII", "FINANCIAL", "SENSITIVE_PII", "HEALTH"].includes(f.sensitivity);
      const safeTransform = ["hash", "tokenise", "redact", "generalise", "bucket", "truncate_date"].includes(f.transform.kind);
      if (denied && !f.override && !(f.decision === "TRANSFORMED" && safeTransform))
        errs.push(`${e.sourceTable}.${f.sourceColumn}: ${f.sensitivity} requires a protective transform or an explicit override`);
      if (f.decision === "SHARED" && f.transform.kind !== "copy") errs.push(`${e.sourceTable}.${f.sourceColumn}: SHARED must use copy; use TRANSFORMED`);
      if (f.decision === "TRANSFORMED" && f.transform.kind === "copy") errs.push(`${e.sourceTable}.${f.sourceColumn}: TRANSFORMED requires a transform`);
    }
    if (e.partitionBy && !fields.has(e.partitionBy)) errs.push(`${e.servingEntity}: partitionBy ${e.partitionBy} is not an exported field`);
    entities.set(e.servingEntity, fields);
  }
  const capNames = new Set<string>();
  for (const cap of c.capabilities) {
    if (capNames.has(cap.name)) errs.push(`duplicate capability ${cap.name}`);
    capNames.add(cap.name);
    const fields = entities.get(cap.entity);
    if (!fields) { errs.push(`capability ${cap.name}: unknown entity ${cap.entity}`); continue; }
    for (const f of cap.filters) if (!fields.has(f.field)) errs.push(`capability ${cap.name}: filter on non-exported field ${f.field}`);
    for (const r of cap.returns) if (!fields.has(r)) errs.push(`capability ${cap.name}: returns non-exported field ${r}`);
    for (const s of cap.sort) if (!fields.has(s.field)) errs.push(`capability ${cap.name}: sort on non-exported field ${s.field}`);
    if (cap.kind === "aggregate") {
      if (!cap.aggregate) errs.push(`capability ${cap.name}: aggregate kind requires aggregate spec`);
      else {
        if (cap.aggregate.field && !fields.has(cap.aggregate.field)) errs.push(`capability ${cap.name}: aggregate field not exported`);
        for (const g of cap.aggregate.groupBy) if (!fields.has(g)) errs.push(`capability ${cap.name}: groupBy non-exported field ${g}`);
      }
    }
  }
  return errs;
}

/** Exactly which serving fields exist per entity — the lineage "what leaves my network" report. */
export function exposureReport(c: ExposureContract) {
  return c.entities.map((e) => ({
    entity: e.servingEntity,
    source: `${e.sourceSchema}.${e.sourceTable}`,
    rowFilters: e.rowFilters,
    shared: e.fields.filter((f) => f.decision === "SHARED").map((f) => ({ source: f.sourceColumn, serving: f.servingField, sensitivity: f.sensitivity })),
    transformed: e.fields.filter((f) => f.decision === "TRANSFORMED").map((f) => ({ source: f.sourceColumn, serving: f.servingField, transform: f.transform, sensitivity: f.sensitivity })),
    blocked: e.fields.filter((f) => f.decision === "BLOCKED").map((f) => ({ source: f.sourceColumn, sensitivity: f.sensitivity })),
  }));
}
